import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment-timezone'
import Popover from '@material-ui/core/Popover'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import { ReactComponent as CalendarIcon } from 'presentation/assets/icons/calendar.svg'
import { parseISOToDate } from 'presentation/utils/parseISOToDate'
import { formatDateToBR } from 'presentation/utils/formatDateToBR'
import { parseBRToISO } from 'presentation/utils/parseBRToISO'
import { isValidBRDate } from 'presentation/utils/isValidBRDate'
import { dateMask } from 'presentation/utils/masks'
import theme from 'presentation/styles/theme'
import * as S from './styles'

export type DayCell = {
  day: number | null
  date: Date | null
}

export type DatePickerNewProps = {
  mainValue: string
  onMainChange: (date: string) => void
  label?: string
  placeholder?: string
  closeOnChange?: boolean
  required?: boolean
  disabled?: boolean
  error?: string | false
  name?: string
  minDate?: Date
  maxDate?: Date
  yearRange?: { start: number; end: number }
  labelColor?: keyof typeof theme.ds.colors
  labelSize?: keyof typeof theme.ds.typography.size
  labelWeight?: keyof typeof theme.ds.typography.weight
  requiredColor?: keyof typeof theme.ds.colors
  labelOverlapping?: boolean
  icon?: React.ReactNode
  iconIsButton?: boolean
  height?: string
}

type CalendarState = {
  anchorEl: HTMLElement | null
  viewMonth: number
  viewYear: number
}

export const MONTHS_PT = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro'
]

export const WEEKDAYS_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

/**
 * Builds a grid of DayCell objects for the given year and month (0-indexed).
 * Pads the start with null cells to align the first day to the correct weekday (Sunday-first).
 */
export function buildDayGrid(year: number, month: number): DayCell[] {
  const firstDay = new Date(Date.UTC(year, month, 1))
  const startPadding = firstDay.getUTCDay() // 0 = Sunday

  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()

  const cells: DayCell[] = []

  for (let i = 0; i < startPadding; i++) {
    cells.push({ day: null, date: null })
  }

  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({
      day: d,
      date: new Date(Date.UTC(year, month, d))
    })
  }

  return cells
}

const DatePickerNew = ({
  mainValue,
  onMainChange,
  label,
  placeholder,
  closeOnChange = false,
  required = false,
  disabled = false,
  error = false,
  name,
  minDate,
  maxDate,
  yearRange,
  labelColor = 'neutral900',
  labelSize = 'small',
  labelWeight = 'normal',
  requiredColor = 'danger200',
  labelOverlapping = false,
  icon = <CalendarIcon />,
  iconIsButton = true,
  height = '40px'
}: DatePickerNewProps) => {
  const now = new Date()
  const parsed = parseISOToDate(mainValue)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const pendingOpenRef = useRef(false)
  const isMountedRef = useRef(true)
  const [calendarState, setCalendarState] = useState<CalendarState>({
    anchorEl: null,
    viewMonth: parsed ? parsed.getUTCMonth() : now.getMonth(),
    viewYear: parsed ? parsed.getUTCFullYear() : now.getFullYear()
  })
  const [inputValue, setInputValue] = useState(
    parsed ? formatDateToBR(parsed) : ''
  )

  const currentYear = now.getFullYear()
  const effectiveYearRange = yearRange ?? {
    start: currentYear - 150,
    end: currentYear
  }
  const years = Array.from(
    { length: effectiveYearRange.end - effectiveYearRange.start + 1 },
    (_, i) => effectiveYearRange.start + i
  )

  const effectiveMinDate = minDate
    ? new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())
    : new Date(effectiveYearRange.start, 0, 1)

  const effectiveMaxDate = maxDate
    ? new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())
    : new Date(effectiveYearRange.end, 11, 31)

  const minDateUTC = new Date(
    Date.UTC(
      effectiveMinDate.getFullYear(),
      effectiveMinDate.getMonth(),
      effectiveMinDate.getDate()
    )
  )

  const maxDateUTC = new Date(
    Date.UTC(
      effectiveMaxDate.getFullYear(),
      effectiveMaxDate.getMonth(),
      effectiveMaxDate.getDate()
    )
  )

  const handleIconClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled) {
      pendingOpenRef.current = true
      setCalendarState((prev) => ({
        ...prev,
        anchorEl: wrapperRef.current ?? e.currentTarget
      }))
    }
  }

  const handleClose = () => {
    setCalendarState((prev) => ({ ...prev, anchorEl: null }))
  }

  const handleClear = () => {
    setInputValue('')
    onMainChange('')
  }

  const handleInputChange = (value: string) => {
    setInputValue(value)

    if (isValidBRDate(value)) {
      const iso = parseBRToISO(value)
      onMainChange(iso)
      const date = parseISOToDate(iso)
      if (date) {
        setCalendarState((prev) => ({
          ...prev,
          viewMonth: date.getUTCMonth(),
          viewYear: date.getUTCFullYear()
        }))
      }
    } else {
      onMainChange('')
    }
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value
    // Use setTimeout so the browser has time to move focus to the next element
    // before we check — this fixes MaskedInput not forwarding relatedTarget
    setTimeout(() => {
      if (!isMountedRef.current) return
      // If the calendar icon was just clicked, don't clear state —
      // pendingOpenRef coordinates handleIconClick and handleBlur
      if (pendingOpenRef.current) {
        pendingOpenRef.current = false
        return
      }
      const activeEl = document.activeElement
      const isMovingToInternalElement = wrapperRef.current?.contains(activeEl)
      if (
        !isMovingToInternalElement &&
        (value.includes('_') || !isValidBRDate(value))
      ) {
        setInputValue('')
        onMainChange('')
      }
    }, 0)
  }

  const isMonthDisabled = (month: number): boolean => {
    if (new Date(calendarState.viewYear, month + 1, 0) < effectiveMinDate)
      return true
    if (new Date(calendarState.viewYear, month, 1) > effectiveMaxDate)
      return true
    return false
  }

  const isYearDisabled = (year: number): boolean => {
    if (new Date(year, 11, 31) < effectiveMinDate) return true
    if (new Date(year, 0, 1) > effectiveMaxDate) return true
    return false
  }

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Sync inputValue and calendar view when mainValue changes externally
  useEffect(() => {
    const date = parseISOToDate(mainValue)
    if (date) {
      setInputValue(formatDateToBR(date))
      setCalendarState((prev) => ({
        ...prev,
        viewMonth: date.getUTCMonth(),
        viewYear: date.getUTCFullYear()
      }))
    } else {
      setInputValue('')
    }
  }, [mainValue])

  return (
    <S.Wrapper ref={wrapperRef} data-testid="date-picker-wrapper">
      <TextFieldNew
        mask={dateMask}
        icon={icon}
        iconPosition="insideLeft"
        iconIsButton={iconIsButton}
        keepStyleOnIconIsButton
        onIconClick={handleIconClick}
        clearable
        onClearClick={handleClear}
        value={inputValue}
        label={label}
        labelOverlapping={labelOverlapping}
        placeholder={placeholder}
        required={required}
        requiredColor={requiredColor}
        disabled={disabled}
        error={error}
        name={name}
        height={height}
        labelColor={labelColor}
        labelSize={labelSize}
        labelWeight={labelWeight}
        onInputChange={handleInputChange}
        onBlur={handleBlur}
      />
      <Popover
        open={Boolean(calendarState.anchorEl)}
        anchorEl={calendarState.anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <S.PopoverContent data-testid="date-picker-popover-content">
          <S.DropdownsRow>
            <S.MonthSelect
              value={calendarState.viewMonth}
              onChange={(e) =>
                setCalendarState((prev) => ({
                  ...prev,
                  viewMonth: Number(e.target.value)
                }))
              }
              aria-label="Selecionar mês"
            >
              {MONTHS_PT.map((month, index) => (
                <option
                  key={month}
                  value={index}
                  disabled={isMonthDisabled(index)}
                >
                  {month}
                </option>
              ))}
            </S.MonthSelect>
            <S.YearSelect
              value={calendarState.viewYear}
              onChange={(e) =>
                setCalendarState((prev) => ({
                  ...prev,
                  viewYear: Number(e.target.value)
                }))
              }
              aria-label="Selecionar ano"
            >
              {years.map((year) => (
                <option key={year} value={year} disabled={isYearDisabled(year)}>
                  {year}
                </option>
              ))}
            </S.YearSelect>
          </S.DropdownsRow>
          <S.WeekdaysRow>
            {WEEKDAYS_SHORT.map((day) => (
              <S.WeekdayCell key={day}>{day}</S.WeekdayCell>
            ))}
          </S.WeekdaysRow>
          <S.DayGridWrapper>
            {buildDayGrid(calendarState.viewYear, calendarState.viewMonth).map(
              (cell, index) => {
                if (!cell.day || !cell.date) {
                  return <S.EmptyCell key={`empty-${index}`} />
                }

                const cellISOStr = moment.utc(cell.date).format('YYYY-MM-DD')

                const selectedDate = parseISOToDate(mainValue)
                const isSelected = selectedDate
                  ? cell.date.getUTCFullYear() ===
                      selectedDate.getUTCFullYear() &&
                    cell.date.getUTCMonth() === selectedDate.getUTCMonth() &&
                    cell.date.getUTCDate() === selectedDate.getUTCDate()
                  : false

                const today = new Date()
                const isToday =
                  cell.date.getUTCFullYear() === today.getFullYear() &&
                  cell.date.getUTCMonth() === today.getMonth() &&
                  cell.date.getUTCDate() === today.getDate()

                const isDisabled =
                  cell.date < minDateUTC || cell.date > maxDateUTC

                const ariaLabel = `${cell.day} de ${
                  MONTHS_PT[calendarState.viewMonth]
                } de ${calendarState.viewYear}`

                return (
                  <S.DayButton
                    key={cellISOStr}
                    isSelected={isSelected}
                    isToday={isToday}
                    isDisabled={isDisabled}
                    disabled={isDisabled}
                    aria-label={ariaLabel}
                    aria-pressed={isSelected}
                    onClick={() => {
                      if (!isDisabled) {
                        onMainChange(cellISOStr)
                        if (closeOnChange) {
                          setCalendarState((prev) => ({
                            ...prev,
                            anchorEl: null
                          }))
                        }
                      }
                    }}
                  >
                    {cell.day}
                  </S.DayButton>
                )
              }
            )}
          </S.DayGridWrapper>
        </S.PopoverContent>
      </Popover>
    </S.Wrapper>
  )
}

export default DatePickerNew
